import React, { useEffect, useState } from "react";
import { Navigate } from 'react-router-dom';
import {
    DndContext,
    DragOverlay,
    pointerWithin,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors
} from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy } from "@dnd-kit/sortable";
import ModuleButton from "../elements/admin/ModuleButton.js";
import DroppableModuleContainer from "../elements/admin/DroppableModuleContainer.js";
import { useDragDrop } from "../../hooks/DragDropHook.js";
import {useModuleControls} from "../../hooks/ModuleControlHook.js";
import AddButton from "../elements/admin/AddButton.js";
import InstallersView from "../elements/admin/InstallersView.js";
import CreateInstallerView from "../elements/admin/CreateInstallerView.js";
import { ReactComponent as LockIcon } from "../../images/icons/lock.svg";
import {UserUtils} from "../../utils/UserUtils.js";

const emptyAdminData = {
    cryptoEnabled: false,
    inactiveModules: [],
    activeModules: [],
    installers: []
}

export default function Admin()
{
    const user = UserUtils.getCurrentUser();

    const [refreshKey, setRefreshKey] = useState(0);
    const refresh = () => setRefreshKey(prev => prev + 1);
    const { uninstallModule, loadModule, unloadModule } = useModuleControls(refresh);

    const [adminData, setAdminData] = useState(emptyAdminData);

    useEffect(() =>
    {
        fetch("admin/api/get-admin-data", { credentials: "include" })
            .then(res => res.ok ? res.json() : emptyAdminData)
            .then(setAdminData);
    }, [refreshKey]);

    const [installersViewOpen, setInstallersViewOpen] = useState(false);
    const [createInstallerViewOpen, setCreateInstallerViewOpen] = useState(false);

    const {
        activeModule,
        handleDragStart,
        handleDragEnd
    } = useDragDrop(adminData, setAdminData, loadModule, unloadModule);

    // Configure sensors to require drag distance/delay
    const mouseSensor = useSensor(MouseSensor, {
        activationConstraint: {
            distance: 10, // 10px movement required
        },
    });

    const touchSensor = useSensor(TouchSensor, {
        activationConstraint: {
            delay: 100,
            tolerance: 5,
        },
    });

    const sensors = useSensors(mouseSensor, touchSensor);

    if(user == null || user.roles == null || !user.roles.some(role => role.permsList?.includes("admin")))
    { return <Navigate to="/" replace />; }

    return (
        <div id="admin-content-area">
            <DndContext
                sensors={sensors}
                collisionDetection={pointerWithin}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
            >
                <div className="page-header">
                    <h1 className="page-title">Installed Modules</h1>
                    <div className="divider"></div>
                </div>
                <div id="module-containers">
                    <div>
                        <h1 className="page-sub-title">Inactive</h1>
                        <SortableContext items={adminData.inactiveModules.map(m => m.manifest.moduleId)} strategy={rectSortingStrategy}>
                            <DroppableModuleContainer
                                id="inactive-module-container"
                                contents={
                                    adminData.inactiveModules.map(module => (
                                        <ModuleButton
                                            key={module.manifest.moduleId}
                                            module={module}
                                            moduleControls={{ uninstallModule, loadModule, unloadModule }}
                                        />
                                    ))
                                }
                            />
                        </SortableContext>
                    </div>
                    <div id="module-controls">
                        <h1 className="page-sub-title">&nbsp;</h1>
                        <AddButton
                            setInstallersViewOpen={setInstallersViewOpen}
                            setCreateInstallerViewOpen={setCreateInstallerViewOpen}
                            secureStorageEnabled={adminData.cryptoEnabled}
                        />
                        {
                            adminData.cryptoEnabled && <LockIcon title="Secure Storage Enabled" className="lock-icon"/>
                        }
                    </div>
                    <div>
                        <h1 className="page-sub-title">Active</h1>
                        <SortableContext items={adminData.activeModules.map(m => m.manifest.moduleId)} strategy={rectSortingStrategy}>
                            <DroppableModuleContainer
                                id="active-module-container"
                                contents={
                                    adminData.activeModules.map(module => (
                                        <ModuleButton
                                            key={module.manifest.moduleId}
                                            module={module}
                                            moduleControls={{ uninstallModule, loadModule, unloadModule }}
                                        />
                                    ))
                                }
                            />
                        </SortableContext>
                    </div>
                </div>

                <DragOverlay style={{ zIndex: 10000 }}>
                    {activeModule ? <ModuleButton module={activeModule} isDragging={true} moduleControls={{ uninstallModule, loadModule, unloadModule }} /> : null}
                </DragOverlay>
            </DndContext>

            {
                installersViewOpen &&
                <InstallersView
                    setInstallersViewOpen={setInstallersViewOpen}
                    installedModules={adminData.inactiveModules.map(mod => mod.manifest).concat(adminData.activeModules.map(mod => mod.manifest)) }
                />
            }

            {
                createInstallerViewOpen &&
                <CreateInstallerView mode="github" setCreateInstallerViewOpen={setCreateInstallerViewOpen}/>
            }
        </div>
    );
}